import sortBy from "lodash/sortBy"
import { generatePath } from "react-router-dom"
import { Column, UseTableState } from "../components/Table"
import { FiltersState } from "../states"
import { percentageFormatter } from "../../mp-core/helpers/formatters"
import { FILTER_OPTION_NAME } from "../components/TableRowActions/constants"
import {
  ADDITIONAL_ORDER_REQUEST_FIELDS,
  CONTENT_VIEWS_BY_TYPE_MAP,
  REPORTING_ROUTE_MATCH,
  REQUEST_DEFAULT_ORDER
} from "../constants"
import {
  fetchAdditionalScoresTableData,
  fetchAdditionalSubtestsScoresTableData,
  getTransformedScoresTableData
} from "./tables/insights"
import { Location } from "history"
import { ProgramAndCoursesInfo } from "../../../types/gqlTypes/graphql"
import { fetchAdditionalClassesTableData } from "./tables/organization"
import {
  USAGE_TO_CONTENT_TYPES,
  CONTENT_TYPE_TO_URL_MAPPING
} from "../constants/filters"
import { CubeRequestParams } from "../types"
import { getOmitAndMergedSearchString } from "./nav"
import {
  getWorkkeysReplaceUrlSubpage,
  getWorkkeysReplaceUrlContentType
} from "./workkeysReplaceTerms"

export const formatCorrectAnswers = record => {
  const answers = sortBy(
    Object.entries<any>(
      JSON.parse(record["questions_view.answers"] || "[]") ?? {}
    ),
    answer => answer[0]
  ).map(([_id, data]) => data)

  const correctAnswers = answers.filter(data => data.correct)

  if (correctAnswers.length <= 1) {
    const correctIndex = answers.findIndex(data => data.correct)

    return correctIndex >= 0
      ? [String.fromCharCode("A".charCodeAt(0) + correctIndex)]
      : ["--"]
  }

  return correctAnswers.map(item => item.answer)
}

export const formatStudentAnswer = record => {
  const answers = JSON.parse(record["questions_view.answers"] || "[]") ?? []
  const answersIds = answers.map(item => item.id)
  let answerIndex = null

  if (record["questions_view.testType"] === "adaptive") {
    answerIndex = Number(record["questions_view.responseKey"])
  } else {
    const responseIds =
      (JSON.parse(record["questions_view.response"] || "[]") ?? [])[0]
        ?.response || []

    answerIndex = answersIds.findIndex(id => responseIds.includes(id))
  }

  return answerIndex >= 0
    ? [String.fromCharCode("A".charCodeAt(0) + answerIndex)]
    : ["--"]
}

export const isStudentAnswerCorrect = record => {
  const answers = JSON.parse(record["questions_view.answers"] || "[]") ?? []
  const correctAnswers = answers
    .filter(item => item?.correct)
    ?.map(item => item.id)

  if (record["questions_view.testType"] === "adaptive") {
    const index = Number(record["questions_view.responseKey"])
    const studentAnswer = answers[index]?.id

    return correctAnswers.includes(studentAnswer)
  }

  const responseIds =
    (JSON.parse(record["questions_view.response"] || "[]") ?? [])[0]
      ?.response || []

  return responseIds.some(item => correctAnswers.includes(item))
}

export const getContentTypeFromRecord = (record: any) => {
  const viewName = Object.keys(record || {})[0]?.split(".")[0]

  const contentType = !!viewName ? CONTENT_VIEWS_BY_TYPE_MAP[viewName] : "mixed"

  if (contentType === "mixed") {
    return record?.["content_view_list.type"]
  }

  if( record['type'] ) {
    return record['type']
  }

  return contentType
}

export const getDetailsViewHref =
  (module: string, subpage: string, currentLocation?: Location) =>
  (id: string, details?: any) => {
    if (module === "assignments-drawer") {
      // For content, use content module
      if (subpage === "content") {
        const { record } = details || {}
        const contentType = getContentTypeFromRecord(record)

        if (!contentType) return ""

        const path = generatePath(REPORTING_ROUTE_MATCH.at(-2), {
          module: "content",
          subpage: "content-library",
          entityType: CONTENT_TYPE_TO_URL_MAPPING[contentType] || "",
          entityId: id
        })

        return `${path}${
          !!currentLocation?.search ? currentLocation.search : ""
        }`
      }

      if (subpage === "students" || subpage === "classes") {
        const path = generatePath(REPORTING_ROUTE_MATCH.at(-1), {
          module: "organization",
          subpage,
          entityId: id
        })

        return !!path
          ? `${path}${!!currentLocation?.search ? currentLocation.search : ""}`
          : null
      }
    }

    if (module === "content") {
      const { record } = details || {}

      const basicSubpage = subpage.split("/")[0]
      const contentType = getContentTypeFromRecord(record)

      if (!contentType) return ""

      const urlContentType = CONTENT_TYPE_TO_URL_MAPPING[contentType] || ""
      const transformedContentType =
        getWorkkeysReplaceUrlContentType(urlContentType)

      const path = generatePath(REPORTING_ROUTE_MATCH.at(-2), {
        module,
        subpage: basicSubpage,
        entityType: transformedContentType,
        entityId: id
      })

      return `${path}${!!currentLocation?.search ? currentLocation.search : ""}`
    }

    const transformedSubpage = getWorkkeysReplaceUrlSubpage(subpage)

    // Default case for other modules
    const path = generatePath(REPORTING_ROUTE_MATCH.at(-1), {
      module,
      subpage: transformedSubpage,
      entityId: id
    })

    return !!path
      ? `${path}${!!currentLocation?.search ? currentLocation.search : ""}`
      : null
  }

export const getViewActivityLogHref =
  (subpage: string, currentLocation: Location) => (id: string) => {
    const filterOptionName = FILTER_OPTION_NAME[subpage]

    if (!filterOptionName) return null

    const entityParams = {
      [filterOptionName]: [id]
    }

    const omitKeys = [
      filterOptionName,
      ...(subpage === "teachers-usage" ? ["student"] : []),
      ...(subpage === "students-usage" ? ["teacher"] : [])
    ]

    const updatedSearchString = getOmitAndMergedSearchString({
      search: currentLocation.search || "",
      omitKeys,
      mergeData: entityParams
    })

    return `/reporting/insights/usage/activity-log?${updatedSearchString}`
  }

export const isExportTableActionDisabled = (
  page: string,
  subpage: string,
  filters: FiltersState
): boolean => {
  switch (`${page}/${subpage}`) {
    default:
      return false
  }
}

export const getTransformedTableData = (details: {
  idProp: string
  columns: Column[]
  properties: string[]
  records: any[]
  page: string
  subpage: string
  filtersState: FiltersState
  programsAndCourses: ProgramAndCoursesInfo[]
}): {
  columns: Column[]
  properties: string[]
  records: any[]
} => {
  const {
    idProp,
    columns,
    properties,
    records,
    page,
    subpage,
    filtersState,
    programsAndCourses
  } = details

  if (page === "insights" && subpage === "scores") {
    return getTransformedScoresTableData(
      idProp,
      filtersState,
      programsAndCourses,
      records,
      columns,
      properties
    )
  }

  return { columns, properties, records }
}

export const fetchAdditionalTableData = async (details: {
  page: string
  subpage: string
  filters: FiltersState
  queryFilters: any[]
  queryOrder: Record<string, string>
  timeDimensions: any
  limit: number
  offset: number
  basicDataTablePromise: Promise<any>
  requestParams: Record<string, Record<string, CubeRequestParams>>
}): Promise<any> => {
  const {
    page,
    subpage,
    queryFilters,
    queryOrder,
    timeDimensions,
    limit,
    offset,
    requestParams,
    basicDataTablePromise
  } = details || {}

  // Commented out, because currently was added field to cube `scores_view` view with json of subtests score
  // and now it's no needed to make additional request for getting subtests scores
  // if (page === "insights" && subpage === "scores") {
  //   return fetchAdditionalScoresTableData(details)
  // }

  if (page === "insights" && subpage === "subtests-scores") {
    return fetchAdditionalSubtestsScoresTableData(details)
  }

  if (page === "organization" && subpage === "classes") {
    return fetchAdditionalClassesTableData({
      queryFilters,
      queryOrder,
      timeDimensions,
      limit,
      offset,
      requestParams,
      basicDataTablePromise
    })
  }

  return basicDataTablePromise
}

export const standardsTableFormatter = (record: any, prop: string) => {
  let recordProp = record[prop]

  if (!recordProp || recordProp === "null") {
    return "--"
  }

  if (typeof recordProp === "string") {
    recordProp = record[prop].split(", ")
  }

  if (recordProp.length <= 1) {
    return recordProp[0]
  }

  return `${recordProp[0]} + ${recordProp.length - 1} more`
}

export const assignedPercentTableFormatter = (
  assignedCount: number,
  completedCount: number
) => {
  if (assignedCount === null || assignedCount === undefined) return null
  if (completedCount === null || completedCount === undefined) return null

  // Assigned but not completed.
  const notCompletedPercent = (assignedCount - completedCount) / assignedCount

  return percentageFormatter.format(notCompletedPercent)
}

export const completedPercentTableFormatter = (
  assignedCount: number,
  completedCount: number
) => {
  if (assignedCount === null || assignedCount === undefined) return null
  if (completedCount === null || completedCount === undefined) return null

  const completedPercent = completedCount / assignedCount

  return percentageFormatter.format(completedPercent)
}

export const getFlatColumns = (columns: Column[]) => {
  return columns.reduce((acc, column) => {
    if (!column.columns) {
      return [...acc, column]
    }

    return [...acc, column, ...getFlatColumns(column.columns)]
  }, [])
}

export const formatMilliseconds = (record: any, prop: string) => {
  if (!record[prop] || isNaN(Number(record[prop]))) {
    return record[prop]
  }

  return record[prop] / 1000
}

export const getFullTestName = (program: string, subject: string) => {
  if (!!program && !!subject) {
    const upperCaseProgram = program.toUpperCase()

    return `${upperCaseProgram} ${subject}`
  }

  return "--"
}

export const getContentRecordLinkFromUsageContentType = (
  usageContentType: string,
  contentId: string
) => {
  const contentType = USAGE_TO_CONTENT_TYPES[usageContentType]
  const urlContentType = CONTENT_TYPE_TO_URL_MAPPING[contentType]
  const transformedContentType = getWorkkeysReplaceUrlContentType(
    urlContentType || ""
  )

  return !!contentId && !!transformedContentType
    ? `/reporting/content/content-library/${transformedContentType}`
    : null
}

export const getOrgViewNavigationLink = (
  record: any,
  link: string,
  idProp: string,
  nameProp?: string
) => {
  return !!record[idProp] && !!record[nameProp ?? idProp] ? link : null
}

export const getTableOrderRequest = (args: {
  page: string
  subpage: string
  tableState: UseTableState
}) => {
  const { page, subpage, tableState } = args

  const defaultOrder = REQUEST_DEFAULT_ORDER[page]?.[subpage]
  const defaultOrderFields = Object.keys(defaultOrder || {})

  if (!!tableState.sortBy) {
    const additionalOrderRequestFields =
      ADDITIONAL_ORDER_REQUEST_FIELDS[tableState.sortBy]

    return {
      [tableState.sortBy]: tableState.order,
      ...(!!additionalOrderRequestFields?.length
        ? additionalOrderRequestFields.reduce(
            (res, item) => ({
              ...res,
              [item]: tableState.order
            }),
            {}
          )
        : {}),
      ...(!!defaultOrderFields?.length
        ? defaultOrderFields.reduce(
            (res, field) => ({
              ...res,
              [field]: tableState.order
            }),
            {}
          )
        : {})
    }
  }

  if (!!defaultOrder) {
    return defaultOrder
  }

  return null
}

export const getCountLinkFilters = (
  page: string,
  subpage: string,
  record: any
): Record<string, string[]> => {
  switch (`${page}/${subpage}`) {
    case "organization/districts": {
      return {
        district: [record["org_view_districts.districtId"]]
      }
    }
    case "organization/schools": {
      return {
        district: [record["org_view_schools.districtId"]]
      }
    }
    case "organization/teachers": {
      const filters: Record<string, string[]> = {}
      if (record["org_view_teachers.districtId"]) {
        filters.district = [record["org_view_teachers.districtId"]]
      }
      if (record["org_view_teachers.schoolId"]) {
        filters.school = [record["org_view_teachers.schoolId"]]
      }
      return filters
    }
    case "organization/classes": {
      const filters: Record<string, string[]> = {}
      if (record["org_view_classes.districtId"]) {
        filters.district = [record["org_view_classes.districtId"]]
      }
      if (record["org_view_classes.schoolId"]) {
        filters.school = [record["org_view_classes.schoolId"]]
      }
      if (record["org_view_classes.teacherId"]) {
        filters.teacher = [record["org_view_classes.teacherId"]]
      }
      return filters
    }
    case "organization/students": {
      const filters: Record<string, string[]> = {}
      if (record["org_view_students.districtId"]) {
        filters.district = [record["org_view_students.districtId"]]
      }
      if (record["org_view_students.schoolId"]) {
        filters.school = [record["org_view_students.schoolId"]]
      }
      return filters
    }

    default:
      return {}
  }
}
